import { useState, useMemo } from 'react';
import { Search, MessageCircle, Loader2 } from 'lucide-react';
import { useGetConversationsQuery } from '@nimbazaar/store';
import type { ConversationWithDetails } from '@nimbazaar/types';

interface ConversationListProps {
  selectedId?: string;
  onSelect: (id: string) => void;
  onlineUsers: Set<string>;
}

export function ConversationList({ selectedId, onSelect, onlineUsers }: ConversationListProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const { data, isLoading } = useGetConversationsQuery({ limit: 50 });
  const conversations = data?.data ?? [];

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return conversations;
    const q = searchQuery.toLowerCase();
    return conversations.filter(
      (c) =>
        c.ad?.title?.toLowerCase().includes(q) ||
        c.otherUser?.name?.toLowerCase().includes(q)
    );
  }, [conversations, searchQuery]);

  function formatTime(dateStr: string | null | undefined) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) {
      return d.toLocaleDateString([], { weekday: 'short' });
    }
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }

  return (
    <>
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100">
        <h2 className="text-lg font-bold text-gray-900 mb-3">Messages</h2>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search conversations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-primary-500 animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <MessageCircle className="w-10 h-10 text-gray-200 mb-3" />
            <p className="text-sm text-gray-400 text-center">
              {searchQuery ? 'No conversations match your search' : 'No conversations yet'}
            </p>
          </div>
        ) : (
          filtered.map((conv) => (
            <ConversationItem
              key={conv.id}
              conversation={conv}
              isSelected={conv.id === selectedId}
              isOnline={conv.otherUser ? onlineUsers.has(conv.otherUser.id) : false}
              onClick={() => onSelect(conv.id)}
              formatTime={formatTime}
            />
          ))
        )}
      </div>
    </>
  );
}

interface ConversationItemProps {
  conversation: ConversationWithDetails;
  isSelected: boolean;
  isOnline: boolean;
  onClick: () => void;
  formatTime: (dateStr: string | null | undefined) => string;
}

function ConversationItem({
  conversation,
  isSelected,
  isOnline,
  onClick,
  formatTime,
}: ConversationItemProps) {
  const { ad, otherUser, lastMessage, unreadCount } = conversation;
  const adImage = ad?.images?.[0]?.url;
  const userName = otherUser?.name || 'Unknown';
  const initials = userName
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <button
      onClick={onClick}
      className={`w-full flex items-start gap-3 px-4 py-3 transition-colors text-left hover:bg-gray-50 ${
        isSelected ? 'bg-primary-50 border-r-2 border-primary-500' : ''
      }`}
    >
      {/* Avatar */}
      <div className="relative shrink-0">
        {otherUser.avatar ? (
          <img
            src={otherUser.avatar}
            alt={userName}
            className="w-12 h-12 rounded-full object-cover"
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
            <span className="text-sm font-semibold text-primary-600">{initials}</span>
          </div>
        )}
        {isOnline && (
          <span className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-semibold text-gray-900 truncate">{userName}</p>
          <span className="text-xs text-gray-400 shrink-0">
            {formatTime(lastMessage?.createdAt || null)}
          </span>
        </div>
        <p className="text-xs text-gray-500 truncate mt-0.5">{ad?.title || 'Deleted ad'}</p>
        <div className="flex items-center justify-between gap-2 mt-1">
          <p className="text-xs text-gray-400 truncate">
            {lastMessage
              ? lastMessage.type === 'IMAGE'
                ? '📷 Photo'
                : lastMessage.content
              : 'No messages yet'}
          </p>
          {unreadCount > 0 && (
            <span className="shrink-0 bg-primary-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </div>
      </div>

      {/* Ad Thumbnail */}
      {adImage && (
        <img
          src={adImage}
          alt=""
          className="w-10 h-10 rounded-lg object-cover shrink-0"
        />
      )}
    </button>
  );
}
